import { useCombobox } from "downshift"
import { useEffect, useMemo } from "react"
import { PopoverTextInput } from "shared-web/components/base/overlay/Popover"
import {
  PopoverComboboxItem,
  PopoverSelectListItem,
} from "shared-web/components/composite/popover"

import { PopoverList, PopoverListEmpty } from "components/composite/popover"

import { PopoverMultiSelectComboboxProps } from "./types"

function PopoverMultiSelectCombobox({
  items,
  selected,
  onSelect,
  onChangeQuery,
}: PopoverMultiSelectComboboxProps) {
  useEffect(() => {
    return () => {
      onChangeQuery("")
    }
  }, [onChangeQuery])

  const selectedKeys = useMemo(() => {
    return new Set(selected.map((item) => item.key))
  }, [selected])

  const { getMenuProps, highlightedIndex, getItemProps, getInputProps } =
    useCombobox<PopoverComboboxItem>({
      defaultInputValue: "",
      defaultHighlightedIndex: 0,
      onInputValueChange({ inputValue }) {
        onChangeQuery(inputValue ?? "")
      },
      items,
      selectedItem: null,
      onSelectedItemChange({ selectedItem }) {
        onSelect(selectedItem)
      },
      itemToString: (item) => item?.label ?? "",
      itemToKey: (item) => item?.key ?? "",
      stateReducer: (state, { type, changes }) => {
        const defaultChanges = {
          ...changes,
          isOpen: true,
        }
        switch (type) {
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
            return {
              ...defaultChanges,
              highlightedIndex: state.highlightedIndex,
              inputValue: "",
            }
          case useCombobox.stateChangeTypes.InputKeyDownEscape:
          case useCombobox.stateChangeTypes.InputBlur:
            return {
              ...defaultChanges,
              inputValue: "",
            }
          default:
            return defaultChanges
        }
      },
      isOpen: true,
    })

  return (
    <>
      <PopoverTextInput autoFocus {...getInputProps()} />
      <PopoverList {...getMenuProps()}>
        {items.length === 0 && <PopoverListEmpty />}
        {items.map((item, index) => (
          <PopoverSelectListItem
            key={item.key}
            label={item.label}
            accessory={item.accessory}
            content={item.content}
            isHighlighted={index === highlightedIndex}
            isSelected={selectedKeys.has(item.key)}
            {...getItemProps({ item, index })}
          />
        ))}
      </PopoverList>
    </>
  )
}

export default PopoverMultiSelectCombobox
